import React, { useEffect, useState, Fragment, useRef } from "react";
import { Formik, Form, Field } from "formik";
import { SendNotifySchema } from "../../Helpers/Constants/Schemas";
import { SendNotifyFields } from "../../Helpers/Constants/InitialValues";
import API from "../../Helpers/Constants/EndPoints/Api";
import Axios from "../../Helpers/Universal/Axios/Axios";
import Paginate from "../Common/Paginate";
import { LIMIT, OFFSET } from "../../Helpers/Constants/Default";
import { useSelector } from "react-redux";
import Loader from "../Common/Loader";
import {
  CreatedAt,
  TruncateText,
} from "../../Helpers/Universal/Function/common";
import Swal from "sweetalert2";
import DataNotFound from "../Common/DataNotFound";

// Custom dropdown component with tree structure
const TreeDropdown = ({
  options,
  selected,
  setSelected,
  loadSupervisors,
  loadAgents,
  setOptions,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleSelect = (item, parentId) => {
    setSelected((prev) => {
      let updated = { ...prev };
      if (item.type === "manager") {
        updated.managerIds = updated.managerIds.includes(item.id)
          ? updated.managerIds.filter((x) => x !== item.id)
          : [...updated.managerIds, item.id];
        if (!item.children.length && !item.childrenLoaded)
          loadSupervisors(item.id);
      } else if (item.type === "supervisor") {
        updated.supervisorIds = updated.supervisorIds.includes(item.id)
          ? updated.supervisorIds.filter((x) => x !== item.id)
          : [...updated.supervisorIds, item.id];
        if (!item.children.length && !item.childrenLoaded)
          loadAgents(parentId, item.id);
      } else if (item.type === "agent") {
        updated.agentIds = updated.agentIds.includes(item.id)
          ? updated.agentIds.filter((x) => x !== item.id)
          : [...updated.agentIds, item.id];
      }
      return updated;
    });
  };

  const toggleExpand = (item) => {
    setOptions((prev) =>
      prev.map((opt) =>
        opt.id === item.id ? { ...opt, expanded: !opt.expanded } : opt
      )
    );
  };

  // Select all items of a specific type under a parent
  const selectAll = (type, parentId = null, items = []) => {
    setSelected((prev) => {
      const updated = { ...prev };
      const ids = items.map((item) => item.id);

      if (type === "manager") {
        updated.managerIds = [...new Set([...updated.managerIds, ...ids])];
        // Load supervisors for any manager that hasn't been loaded yet
        items.forEach((item) => {
          if (!item.children.length && !item.childrenLoaded) {
            loadSupervisors(item.id);
          }
        });
      } else if (type === "supervisor") {
        updated.supervisorIds = [
          ...new Set([...updated.supervisorIds, ...ids]),
        ];
        // Load agents for any supervisor that hasn't been loaded yet
        items.forEach((item) => {
          if (!item.children.length && !item.childrenLoaded && parentId) {
            loadAgents(parentId, item.id);
          }
        });
      } else if (type === "agent") {
        updated.agentIds = [...new Set([...updated.agentIds, ...ids])];
      }

      return updated;
    });
  };

  // Deselect all items of a specific type under a parent
  const deselectAll = (type, items = []) => {
    setSelected((prev) => {
      const updated = { ...prev };
      const ids = items.map((item) => item.id);

      if (type === "manager") {
        updated.managerIds = updated.managerIds.filter(
          (id) => !ids.includes(id)
        );
      } else if (type === "supervisor") {
        updated.supervisorIds = updated.supervisorIds.filter(
          (id) => !ids.includes(id)
        );
      } else if (type === "agent") {
        updated.agentIds = updated.agentIds.filter((id) => !ids.includes(id));
      }

      return updated;
    });
  };

  const renderTree = (items, level = 0, parentId = null) => {
    // Check if all items at this level are selected
    const allSelected =
      items.length > 0 &&
      items.every((item) => selected[`${item.type}Ids`]?.includes(item.id));

    // Check if some items at this level are selected (for indeterminate state)
    const someSelected =
      items.length > 0 &&
      items.some((item) => selected[`${item.type}Ids`]?.includes(item.id)) &&
      !allSelected;

    return (
      <div>
        {/* Select All option for this level if there are items */}
        {items.length > 0 && level === 0 && (
          <div style={{ paddingLeft: level * 20, marginBottom: "10px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                background: "rgba(255, 255, 255, 0.1)",
                padding: "5px 10px",
                borderRadius: "4px",
              }}
            >
              <input
                type="checkbox"
                checked={allSelected}
                ref={(el) => {
                  if (el) {
                    el.indeterminate = someSelected;
                  }
                }}
                onChange={() => {
                  if (allSelected) {
                    deselectAll("manager", items);
                  } else {
                    selectAll("manager", null, items);
                  }
                }}
                style={{ marginRight: "8px" }}
              />
              <span
                style={{
                  color: "white",
                  fontWeight: "bold",
                  cursor: "pointer",
                }}
                onClick={() => {
                  if (allSelected) {
                    deselectAll("manager", items);
                  } else {
                    selectAll("manager", null, items);
                  }
                }}
              >
                Select All Managers
              </span>
            </div>
          </div>
        )}

        {items.map((item) => (
          <div key={item.id} style={{ paddingLeft: level * 20 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "5px",
                background:
                  level === 0
                    ? "linear-gradient(90deg, rgba(63,94,251,0.2) 0%, rgba(160, 233, 255, 0.2) 100%)"
                    : level === 1
                    ? "linear-gradient(90deg, rgba(63,94,251,0.15) 0%, rgba(163, 246, 255, 0.15) 100%)"
                    : "transparent",
                padding: "5px 10px",
                borderRadius: "4px",
              }}
            >
              <input
                type="checkbox"
                checked={
                  selected[`${item.type}Ids`]?.includes(item.id) || false
                }
                onChange={() => {
                  toggleSelect(item, item.parentId);
                  setIsOpen(true); // Keep dropdown open after selection
                }}
                style={{ marginRight: "8px" }}
              />
              <span
                style={{
                  color: "white",
                  fontWeight: level === 0 ? "bold" : "normal",
                  cursor: "pointer",
                  flex: 1,
                }}
                onClick={() => {
                  toggleSelect(item, item.parentId);
                  setIsOpen(true); // Keep dropdown open after selection
                }}
              >
                {item.label}
              </span>
              {item.children?.length > 0 && (
                <span
                  style={{
                    marginLeft: "8px",
                    fontSize: "12px",
                    cursor: "pointer",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleExpand(item);
                  }}
                >
                  {item.expanded ? "▼" : "►"}
                </span>
              )}
            </div>

            {/* Select All option for supervisors when expanded */}
            {item.type === "manager" &&
              item.expanded &&
              item.children.length > 0 && (
                <div
                  style={{ paddingLeft: (level + 1) * 20, marginBottom: "5px" }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      background: "rgba(255, 255, 255, 0.08)",
                      padding: "3px 10px",
                      borderRadius: "4px",
                      marginTop: "5px",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={item.children.every((child) =>
                        selected.supervisorIds.includes(child.id)
                      )}
                      ref={(el) => {
                        if (el) {
                          const someSelected = item.children.some((child) =>
                            selected.supervisorIds.includes(child.id)
                          );
                          const allSelected = item.children.every((child) =>
                            selected.supervisorIds.includes(child.id)
                          );
                          el.indeterminate = someSelected && !allSelected;
                        }
                      }}
                      onChange={() => {
                        const allSelected = item.children.every((child) =>
                          selected.supervisorIds.includes(child.id)
                        );

                        if (allSelected) {
                          deselectAll("supervisor", item.children);
                        } else {
                          selectAll("supervisor", item.id, item.children);
                        }
                      }}
                      style={{ marginRight: "8px" }}
                    />
                    <span
                      style={{
                        color: "white",
                        fontSize: "0.9em",
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        const allSelected = item.children.every((child) =>
                          selected.supervisorIds.includes(child.id)
                        );

                        if (allSelected) {
                          deselectAll("supervisor", item.children);
                        } else {
                          selectAll("supervisor", item.id, item.children);
                        }
                      }}
                    >
                      Select All Supervisors
                    </span>
                  </div>
                </div>
              )}

            {/* Select All option for agents when expanded */}
            {item.type === "supervisor" &&
              item.expanded &&
              item.children.length > 0 && (
                <div
                  style={{ paddingLeft: (level + 1) * 20, marginBottom: "5px" }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      background: "rgba(255, 255, 255, 0.06)",
                      padding: "3px 10px",
                      borderRadius: "4px",
                      marginTop: "5px",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={item.children.every((child) =>
                        selected.agentIds.includes(child.id)
                      )}
                      ref={(el) => {
                        if (el) {
                          const someSelected = item.children.some((child) =>
                            selected.agentIds.includes(child.id)
                          );
                          const allSelected = item.children.every((child) =>
                            selected.agentIds.includes(child.id)
                          );
                          el.indeterminate = someSelected && !allSelected;
                        }
                      }}
                      onChange={() => {
                        const allSelected = item.children.every((child) =>
                          selected.agentIds.includes(child.id)
                        );

                        if (allSelected) {
                          deselectAll("agent", item.children);
                        } else {
                          selectAll("agent", null, item.children);
                        }
                      }}
                      style={{ marginRight: "8px" }}
                    />
                    <span
                      style={{
                        color: "white",
                        fontSize: "0.9em",
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        const allSelected = item.children.every((child) =>
                          selected.agentIds.includes(child.id)
                        );

                        if (allSelected) {
                          deselectAll("agent", item.children);
                        } else {
                          selectAll("agent", null, item.children);
                        }
                      }}
                    >
                      Select All Agents
                    </span>
                  </div>
                </div>
              )}

            {item.children?.length > 0 &&
              item.expanded &&
              renderTree(item.children, level + 1, item.id)}
          </div>
        ))}
      </div>
    );
  };

  const selectedCount =
    selected.managerIds.length +
    selected.supervisorIds.length +
    selected.agentIds.length;
  const displayText =
    selectedCount > 0
      ? `${selectedCount} selected`
      : "Select Managers / Supervisors / Agents";

  return (
    <div style={{ position: "relative" }} ref={dropdownRef}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        style={{
          border: "1px solid #2d3748",
          borderRadius: "4px",
          padding: "8px 12px",
          color: "white",
          cursor: "pointer",
          minHeight: "38px",
          display: "flex",
          alignItems: "center",
          fontWeight: "bold",
        }}
      >
        {displayText}
      </div>
      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            backgroundColor: "#1f2a40",
            border: "1px solid #2d3748",
            borderRadius: "4px",
            padding: "10px",
            maxHeight: "300px",
            overflowY: "auto",
            zIndex: 1000,
            marginTop: "5px",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          }}
        >
          {options.length > 0 ? (
            renderTree(options)
          ) : (
            <div style={{ color: "white", padding: "10px" }}>Loading...</div>
          )}
        </div>
      )}
    </div>
  );
};

const Notification = () => {
  const paginate = useSelector((val) => val.paginate);
  const [data, setData] = useState([]);
  const [visible, setVisible] = useState({ loader: false });
  const [isAllSelection, setIsAllSelection] = useState({
    allUsers: false,
    allManagers: false,
    allSupervisors: false,
    allAgents: false,
  });

  const [filter, setFilter] = useState({
    limit: LIMIT,
    offset: paginate?.notify || OFFSET,
  });

  const [initialValues] = useState({
    ...SendNotifyFields.required,
    title: "",
    body: "",
  });

  // 🔹 Hierarchical state
  const [options, setOptions] = useState([]); // managers + children
  const [selected, setSelected] = useState({
    managerIds: [],
    supervisorIds: [],
    agentIds: [],
  });

  // ✅ Fetch Managers initially
  useEffect(() => {
    const fetchManagers = async () => {
      try {
        const res = await Axios.get(API.NOTIFICATION.GET_MANAGER);
        const managers = res.data?.data || [];
        console.log("Managers:", managers);
        setOptions(
          managers.map((m) => ({
            id: m._id,
            label: m.name || "Unnamed Manager",
            type: "manager",
            children: [],
            childrenLoaded: false,
            parentId: null,
            expanded: false,
          }))
        );
      } catch (error) {
        console.error("Error fetching managers:", error);
      }
    };
    fetchManagers();
  }, []);

  // ✅ Load Supervisors
  const loadSupervisors = async (managerId) => {
    try {
      const res = await Axios.post(API.NOTIFICATION.GET_SUPERVISOR, {
        parentId: [managerId],
      });
      const supervisors = res.data?.data || [];
      console.log("Supervisors for manager", managerId, ":", supervisors);

      setOptions((prev) =>
        prev.map((m) =>
          m.id === managerId
            ? {
                ...m,
                children: supervisors.map((s) => ({
                  id: s._id,
                  label: s.name || "Unnamed Supervisor",
                  type: "supervisor",
                  children: [],
                  childrenLoaded: false,
                  parentId: managerId,
                  expanded: false,
                })),
                childrenLoaded: true,
                expanded: true,
              }
            : m
        )
      );
    } catch (error) {
      console.error("Error fetching supervisors:", error);
    }
  };

  // ✅ Load Agents
  const loadAgents = async (managerId, supervisorId) => {
    try {
      const res = await Axios.post(API.NOTIFICATION.GET_AGENT, {
        managerId: [managerId],
        supervisorId: [supervisorId],
      });
      const agents = res.data?.data || [];
      console.log("Agents for supervisor", supervisorId, ":", agents);

      setOptions((prev) =>
        prev.map((m) =>
          m.id === managerId
            ? {
                ...m,
                children: m.children.map((s) =>
                  s.id === supervisorId
                    ? {
                        ...s,
                        children: agents.map((a) => ({
                          id: a._id,
                          label: a.name || "Unnamed Agent",
                          type: "agent",
                          parentId: supervisorId,
                        })),
                        childrenLoaded: true,
                        expanded: true,
                      }
                    : s
                ),
              }
            : m
        )
      );
    } catch (error) {
      console.error("Error fetching agents:", error);
    }
  };

  // ✅ Submit
  // const handleSubmit = async (values) => {
  //   try {
  //     var payload ;
  //     if (
  //       selected.managerIds.length == 0 &&
  //       selected.supervisorIds.length == 0 &&
  //       selected.agentIds.length == 0
  //     ) {
  //       payload = {
  //         title: values.title,
  //         body: values.body,
  //         managerIds: selected.managerIds,
  //         supervisorIds: selected.supervisorIds,
  //         agentIds: selected.agentIds,
  //         isAllManagers: true,
  //         isAllSupervisors: true,
  //         isAllAgents: true,
  //       };
  //     } else {
  //       payload = {
  //         title: values.title,
  //         body: values.body,
  //         managerIds: selected.managerIds,
  //         supervisorIds: selected.supervisorIds,
  //         agentIds: selected.agentIds,
  //       };
  //     }
  //     await Axios.post(API.NOTIFICATION.SEND_NOTIFY, payload);
  //     getData();
  //     setSelected({ managerIds: [], supervisorIds: [], agentIds: [] });
  //     Swal.fire("Success", "Notification Sent!", "success");
  //   } catch (err) {
  //     console.log("ERROR==>", err);
  //     Swal.fire("Error", "Failed to send notification", "error");
  //   }
  // };

  // const handleSubmit = async (values) => {
  //   try {
  //     let payload = {
  //       title: values.title,
  //       body: values.body,
  //       managerIds: selected.managerIds,
  //       supervisorIds: selected.supervisorIds,
  //       agentIds: selected.agentIds,
  //       isAllManagers: false,
  //       isAllSupervisors: false,
  //       isAllAgents: false,
  //       isAllUsers: false,
  //     };

  //     // Quick-select buttons:
  //     if (isAllSelection.allUsers) {
  //       payload.isAllUsers = true;
  //     } else if (isAllSelection.allManagers) {
  //       payload.isAllManagers = true;
  //     } else if (isAllSelection.allSupervisors) {
  //       payload.isAllSupervisors = true;
  //     } else if (isAllSelection.allAgents) {
  //       payload.isAllAgents = true;
  //     }
  //     // If nothing selected and no quick button → send to all
  //     else if (
  //       selected.managerIds.length === 0 &&
  //       selected.supervisorIds.length === 0 &&
  //       selected.agentIds.length === 0
  //     ) {
  //       payload.isAllUsers = true;
  //     }

  //     await Axios.post(API.NOTIFICATION.SEND_NOTIFY, payload);

  //     getData();
  //     setSelected({ managerIds: [], supervisorIds: [], agentIds: [] });
  //     setIsAllSelection({
  //       allUsers: false,
  //       allManagers: false,
  //       allSupervisors: false,
  //       allAgents: false,
  //     });

  //     Swal.fire("Success", "Notification Sent!", "success");
  //   } catch (err) {
  //     console.error("ERROR==>", err);
  //     Swal.fire("Error", "Failed to send notification", "error");
  //   }
  // };

  const handleSubmit = async (values) => {
  try {
    let payload = {
      title: values.title,
      body: values.body,
      managerIds: selected.managerIds,
      supervisorIds: selected.supervisorIds,
      agentIds: selected.agentIds,
      isAllManagers: false,
      isAllSupervisors: false,
      isAllAgents: false,
      isAllUsers: false,
    };

    // Quick select checkboxes
    if (isAllSelection.allUsers) payload.isAllUsers = true;
    else if (isAllSelection.allManagers) payload.isAllManagers = true;
    else if (isAllSelection.allSupervisors) payload.isAllSupervisors = true;
    else if (isAllSelection.allAgents) payload.isAllAgents = true;

    // If NO selection and NO checkbox → default: all users
    else if (
      selected.managerIds.length === 0 &&
      selected.supervisorIds.length === 0 &&
      selected.agentIds.length === 0
    ) {
      payload.isAllUsers = true;
    }

    await Axios.post(API.NOTIFICATION.SEND_NOTIFY, payload);

    getData();
    setSelected({ managerIds: [], supervisorIds: [], agentIds: [] });
    setIsAllSelection({
      allUsers: false,
      allManagers: false,
      allSupervisors: false,
      allAgents: false,
    });

    Swal.fire("Success", "Notification Sent!", "success");
  } catch (err) {
    Swal.fire("Error", "Failed to send notification", "error");
  }
};

  const getData = async () => {
    try {
      setVisible((prev) => ({ ...prev, loader: true }));
      const { status, data } = await Axios.get(
        API.NOTIFICATION.GET_NOTIFY,
        filter
      );
      if (status) setData(data);
    } catch (err) {
      console.log("ERROR==>", err);
    } finally {
      setVisible((prev) => ({ ...prev, loader: false }));
    }
  };

  const handleViewPopup = (data) => {
    Swal.fire({
      title: data.title,
      text: data.body,
      confirmButtonText: "Close",
    });
  };

  useEffect(() => {
    getData();
  }, [filter]);

  return (
    <Fragment>
      {visible.loader && <Loader />}

      {/* Form Section with Gradient Header */}
      <div
        style={{
          padding: "15px",
          borderRadius: "8px",
          marginBottom: "20px",
        }}
      >
        <Formik
          initialValues={initialValues}
          validationSchema={SendNotifySchema}
          onSubmit={(values, { resetForm }) => {
            handleSubmit(values);
            resetForm();
          }}
        >
          {({ isSubmitting, errors, touched }) => (
            <Form className="page-from">
              <div className="inner-form">
                <div className="row">
                  {/* Title */}
                  <div className="col-md-4 mb-3">
                    <label style={{ color: "white", fontWeight: "bold" }}>
                      Title
                    </label>
                    <Field className="form-control" type="text" name="title" />
                    {errors.title && touched.title && (
                      <div className="error">{errors.title}</div>
                    )}
                  </div>

                  {/* Message */}
                  <div className="col-md-9 mb-3">
                    <label style={{ color: "white", fontWeight: "bold" }}>
                      Message
                    </label>
                    <Field
                      className="form-control"
                      component="textarea"
                      name="body"
                    />
                    {errors.body && touched.body && (
                      <div className="error">{errors.body}</div>
                    )}
                  </div>

                  {/* Single Dropdown with Tree */}
                  <div className="col-md-9 mb-3">
                    <label style={{ color: "white", fontWeight: "bold" }}>
                      Send To
                    </label>

                    <div className="col-md-9 mb-3">
             

                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "6px",
                          padding: "10px 0",
                        }}
                      >
                        {/* All Users */}
                        <label style={{ color: "white", cursor: "pointer" }}>
                          <input
                            type="checkbox"
                            checked={isAllSelection.allUsers}
                            onChange={() => {
                              setSelected({
                                managerIds: [],
                                supervisorIds: [],
                                agentIds: [],
                              });
                              setIsAllSelection({
                                allUsers: !isAllSelection.allUsers,
                                allManagers: false,
                                allSupervisors: false,
                                allAgents: false,
                              });
                            }}
                            style={{ marginRight: "8px" }}
                          />
                          All Users
                        </label>

                        {/* All Managers */}
                        <label style={{ color: "white", cursor: "pointer" }}>
                          <input
                            type="checkbox"
                            checked={isAllSelection.allManagers}
                            onChange={() => {
                              setSelected({
                                managerIds: [],
                                supervisorIds: [],
                                agentIds: [],
                              });
                              setIsAllSelection({
                                allUsers: false,
                                allManagers: !isAllSelection.allManagers,
                                allSupervisors: false,
                                allAgents: false,
                              });
                            }}
                            style={{ marginRight: "8px" }}
                          />
                          All Managers
                        </label>

                        {/* All Supervisors */}
                        <label style={{ color: "white", cursor: "pointer" }}>
                          <input
                            type="checkbox"
                            checked={isAllSelection.allSupervisors}
                            onChange={() => {
                              setSelected({
                                managerIds: [],
                                supervisorIds: [],
                                agentIds: [],
                              });
                              setIsAllSelection({
                                allUsers: false,
                                allManagers: false,
                                allSupervisors: !isAllSelection.allSupervisors,
                                allAgents: false,
                              });
                            }}
                            style={{ marginRight: "8px" }}
                          />
                          All Supervisors
                        </label>

                        {/* All Agents */}
                        <label style={{ color: "white", cursor: "pointer" }}>
                          <input
                            type="checkbox"
                            checked={isAllSelection.allAgents}
                            onChange={() => {
                              setSelected({
                                managerIds: [],
                                supervisorIds: [],
                                agentIds: [],
                              });
                              setIsAllSelection({
                                allUsers: false,
                                allManagers: false,
                                allSupervisors: false,
                                allAgents: !isAllSelection.allAgents,
                              });
                            }}
                            style={{ marginRight: "8px" }}
                          />
                          All Agents
                        </label>
                      </div>
                    </div>

                    <TreeDropdown
                      options={options}
                      selected={selected}
                      setSelected={setSelected}
                      setOptions={setOptions}
                      loadSupervisors={loadSupervisors}
                      loadAgents={loadAgents}
                    />
                  </div>

                  {/* Submit */}
                  <div className="col-md-12 mb-3">
                    <button
                      disabled={isSubmitting}
                      className="btn bg-gradient-dark mt-3 d-block btn-lg mb-1"
                      type="submit"
                      style={{
                        border: "none",
                      }}
                    >
                      {isSubmitting ? "Sending..." : "Send"}
                    </button>
                  </div>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>

      <div
        style={{
          padding: "15px",
          borderRadius: "8px",
        }}
      >
        {/* Table */}
        {data?.data?.length === 0 ? (
          <DataNotFound />
        ) : (
          <Fragment>
            <div className="overflow-table">
              <table className="page-table cebtertable">
                <thead>
                  <tr
                    style={{
                      background:
                        "linear-gradient(90deg, rgba(63,94,251,0.4) 0%, rgba(198, 230, 255, 0.4) 100%)",
                    }}
                  >
                    <th>#</th>
                    <th>Date</th>
                    <th>Title</th>
                    <th>Body</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.data?.map((val, i) => (
                    <tr
                      key={val._id}
                      style={{
                        background:
                          i % 2 === 0
                            ? "rgba(255,255,255,0.05)"
                            : "rgba(0,0,0,0.05)",
                      }}
                    >
                      <td>{++i + filter.offset}</td>
                      <td>{CreatedAt(val.createdAt)}</td>
                      <td>{val.title}</td>
                      <td className="msg-body">{TruncateText(val.body, 50)}</td>
                      <td
                        className="td-btn"
                        onClick={() => handleViewPopup(val)}
                      >
                        <span
                          style={{
                            padding: "5px 10px",
                            borderRadius: "4px",
                            cursor: "pointer",
                          }}
                        >
                          view
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Paginate
              count={data?.count}
              filter={filter}
              setFilter={setFilter}
              sliceKey={"notify"}
              forcePage={paginate?.notify / 10}
            />
          </Fragment>
        )}
      </div>
    </Fragment>
  );
};

export default Notification;
